'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type {
  CoPilotSessionExportFormat,
  CoPilotSessionExportArtifact,
} from '@terra/shared-dto-chat-types';

export type DownloadCoPilotSessionExport = (params: {
  format: CoPilotSessionExportFormat;
  threadId: string;
  applicationId: string;
  sessionStatus?: CoPilotSessionExportArtifact['sessionStatus'];
  requestFilename?: string;
  attachmentFilename?: string;
  csvObjectKinds?: CoPilotSessionExportArtifact['csvObjectKinds'];
  csvSources?: CoPilotSessionExportArtifact['csvSources'];
  csvKind?: CoPilotSessionExportArtifact['csvKind'];
  markdownKind?: CoPilotSessionExportArtifact['markdownKind'];
}) => Promise<void>;

type ChatSessionExportContextValue = {
  downloadSessionExport?: DownloadCoPilotSessionExport;
};

const ChatSessionExportContext = createContext<
  ChatSessionExportContextValue | undefined
>(undefined);

type ChatSessionExportProviderProps = {
  children: ReactNode;
  downloadSessionExport?: DownloadCoPilotSessionExport;
};

export function ChatSessionExportProvider({
  children,
  downloadSessionExport,
}: ChatSessionExportProviderProps) {
  const value = useMemo(
    () => ({ downloadSessionExport }),
    [downloadSessionExport],
  );

  return (
    <ChatSessionExportContext.Provider value={value}>
      {children}
    </ChatSessionExportContext.Provider>
  );
}

export function useOptionalChatSessionExport(): ChatSessionExportContextValue {
  return useContext(ChatSessionExportContext) ?? {};
}

export function useChatSessionExportDownload():
  | DownloadCoPilotSessionExport
  | undefined {
  return useOptionalChatSessionExport().downloadSessionExport;
}
